/**
 * The Changes side panel (contracts-plan.md §5.8, §11 tab 4, §11 Global —
 * "every number shows its label and provenance on hover"; R21, R24). Groups
 * `cta_ws_changes` rows into Proposed and Accepted (Rejected collapsed
 * underneath), each row naming sheet!cell, old -> new, its reason, a source
 * badge and, on hover, the source, the source_ref summary and who/when.
 *
 * Per-row Accept/Reject post `{ ids: [id] }`; WorkspaceTab.tsx's toolbar
 * "Accept all" posts `{ all: true }` instead (Appendix A's own selector,
 * resolved server-side over every proposed row).
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { primaryBtnCls, secondaryBtnCls } from '../../../ui';
import type { ChangeSource, WorkspaceChange } from '../../api/workspace';

const SOURCE_LABEL: Record<ChangeSource, string> = {
  manual: 'Manual',
  rate_card: 'Rate card',
  previous_study: 'Previous study',
  assistant: 'Assistant',
  import: 'Import',
};

const SOURCE_PILL_CLS: Record<ChangeSource, string> = {
  manual: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  rate_card: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  previous_study: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  assistant: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  import: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function rowTitle(c: WorkspaceChange): string {
  const parts = [
    `Source: ${SOURCE_LABEL[c.source] ?? c.source}`,
    c.source_ref ? `Source ref: ${JSON.stringify(c.source_ref)}` : 'Source ref: none',
    c.proposed_by ? `Proposed by ${c.proposed_by} on ${c.proposed_at}` : `Recorded ${c.proposed_at}`,
    c.decided_by ? `Decided by ${c.decided_by} on ${c.decided_at}` : null,
  ].filter(Boolean);
  return parts.join(' — ');
}

function ChangeRow({
  change,
  readOnly,
  onFocusCell,
  onAccept,
  onReject,
}: {
  change: WorkspaceChange;
  readOnly: boolean;
  onFocusCell: (change: WorkspaceChange) => void;
  onAccept: (ids: string[]) => void;
  onReject: (ids: string[]) => void;
}) {
  return (
    <li
      data-testid={`ws-change-${change.id}`}
      className="rounded-lg border border-gray-200 p-2.5 text-sm dark:border-gray-700"
    >
      <button type="button" className="block w-full text-left" title={rowTitle(change)} onClick={() => onFocusCell(change)}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
            {change.sheet}!{change.cell_ref}
          </span>
          <span className="tabular-nums text-xs text-gray-500 dark:text-gray-400">
            {change.old_value ?? '—'} &rarr; {change.new_value ?? '—'}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${SOURCE_PILL_CLS[change.source] ?? ''}`}
          >
            {SOURCE_LABEL[change.source] ?? change.source}
          </span>
        </div>
        {change.reason && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{change.reason}</p>}
      </button>
      {!readOnly && change.status === 'proposed' && (
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            data-testid={`ws-change-${change.id}-accept`}
            className={`${primaryBtnCls} !min-h-0 !px-2 !py-1 text-xs`}
            onClick={() => onAccept([change.id])}
          >
            Accept
          </button>
          <button
            type="button"
            data-testid={`ws-change-${change.id}-reject`}
            className={`${secondaryBtnCls} !min-h-0 !px-2 !py-1 text-xs`}
            onClick={() => onReject([change.id])}
          >
            Reject
          </button>
        </div>
      )}
    </li>
  );
}

export interface ChangesPanelProps {
  changes: WorkspaceChange[];
  readOnly: boolean;
  onFocusCell: (change: WorkspaceChange) => void;
  onAccept: (ids: string[]) => void;
  onReject: (ids: string[]) => void;
}

export function ChangesPanel({ changes, readOnly, onFocusCell, onAccept, onReject }: ChangesPanelProps) {
  const [rejectedExpanded, setRejectedExpanded] = useState(false);

  const { proposed, accepted, rejected } = useMemo(() => {
    const p: WorkspaceChange[] = [];
    const a: WorkspaceChange[] = [];
    const r: WorkspaceChange[] = [];
    for (const c of changes) {
      if (c.status === 'proposed') p.push(c);
      else if (c.status === 'accepted') a.push(c);
      else r.push(c);
    }
    return { proposed: p, accepted: a, rejected: r };
  }, [changes]);

  if (changes.length === 0) {
    return <p className="p-3 text-sm text-gray-500 dark:text-gray-400">No changes yet — edit the grid, or apply the rate card or a previous study.</p>;
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <section>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
          Proposed ({proposed.length})
        </h3>
        {proposed.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing proposed.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {proposed.map((c) => (
              <ChangeRow key={c.id} change={c} readOnly={readOnly} onFocusCell={onFocusCell} onAccept={onAccept} onReject={onReject} />
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
          Accepted ({accepted.length})
        </h3>
        {accepted.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing accepted yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {accepted.map((c) => (
              <ChangeRow key={c.id} change={c} readOnly={readOnly} onFocusCell={onFocusCell} onAccept={onAccept} onReject={onReject} />
            ))}
          </ul>
        )}
      </section>

      <section>
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          onClick={() => setRejectedExpanded((v) => !v)}
        >
          {rejectedExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Rejected ({rejected.length})
        </button>
        {rejectedExpanded && (
          <ul className="mt-1.5 flex flex-col gap-2">
            {rejected.map((c) => (
              <ChangeRow key={c.id} change={c} readOnly={readOnly} onFocusCell={onFocusCell} onAccept={onAccept} onReject={onReject} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default ChangesPanel;
